import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faPlus, faUsers } from "@fortawesome/free-solid-svg-icons";
import { Breadcrumb, Button, Card, Col, Form, Pagination, Row, Table } from "@themesberg/react-bootstrap";
import { Link } from "react-router-dom";

import { Routes } from "../routes";
import defaultGroups from "../data/groups";
import defaultGroupTransactions from "../data/groupTransactions";
import { parseMpesaMessage } from "../utils/mpesa";
import { enqueueSync } from "../utils/syncQueue";
import { apiRequest, getAuthToken } from "../utils/api";

const GROUPS_STORAGE_KEY = "treasurer.groups";
const TRANSACTIONS_STORAGE_KEY = "treasurer.groupTransactions";

const loadGroups = () => {
  const stored = localStorage.getItem(GROUPS_STORAGE_KEY);
  if (!stored) {
    return defaultGroups;
  }
  try {
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      return defaultGroups;
    }
    return parsed.map((group) => ({
      ...group,
      createdAt: group.createdAt || new Date().toISOString(),
      frequency: group.frequency || "monthly",
      members: Array.isArray(group.members)
        ? group.members
            .map((member) => {
              if (typeof member === "string") {
                return { id: null, name: member, phone: "", info: "" };
              }
              return {
                id: member?.id ?? null,
                name: member?.name || "",
                phone: member?.phone || "",
                info: member?.info || ""
              };
            })
            .filter((member) => member.name)
        : []
    }));
  } catch (error) {
    return defaultGroups;
  }
};

const loadTransactions = () => {
  const stored = localStorage.getItem(TRANSACTIONS_STORAGE_KEY);
  if (!stored) {
    return defaultGroupTransactions;
  }
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : defaultGroupTransactions;
  } catch (error) {
    return defaultGroupTransactions;
  }
};

const formatDate = (value) => {
  if (!value) {
    return "--";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "--";
  }
  return parsed.toLocaleDateString("en-KE", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
};

const formatAmount = (amount) => {
  if (amount === null || amount === undefined || Number.isNaN(amount)) {
    return "Ksh 0.00";
  }
  return `Ksh ${Number(amount).toLocaleString("en-KE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const normalizePhone = (value = "") => value.replace(/\s+/g, "");
const isValidPhone = (value = "") => {
  if (!value) {
    return true;
  }
  const normalized = normalizePhone(value);
  return /^\+?\d{9,15}$/.test(normalized);
};

export default () => {
  const [groups, setGroups] = useState(loadGroups);
  const [transactions] = useState(loadTransactions);
  const [formValues, setFormValues] = useState({
    name: "",
    purpose: "",
    frequency: "monthly"
  });
  const [addMembers, setAddMembers] = useState(false);
  const [memberInput, setMemberInput] = useState({ name: "", phone: "", info: "" });
  const [members, setMembers] = useState([]);
  const safeMemberInput = memberInput || { name: "", phone: "", info: "" };
  const safeMembers = Array.isArray(members) ? members : [];
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(groups));
  }, [groups]);

  const canSave = useMemo(() => {
    return formValues.name.trim().length > 0 && formValues.purpose.trim().length > 0;
  }, [formValues.name, formValues.purpose]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSave) {
      return;
    }

    const tempId = `local-${Date.now()}`;
    const newGroup = {
      id: tempId,
      localId: tempId,
      name: formValues.name.trim(),
      purpose: formValues.purpose.trim(),
      createdAt: new Date().toISOString(),
      frequency: formValues.frequency,
      members: safeMembers
    };

    setGroups((prev) => [newGroup, ...prev]);
    enqueueSync({
      type: "group.create",
      payload: {
        localId: tempId,
        name: newGroup.name,
        purpose: newGroup.purpose,
        frequency: newGroup.frequency
      }
    });
    const token = getAuthToken();
    if (token && navigator.onLine) {
      try {
        const data = await apiRequest("/groups", {
          method: "POST",
          body: JSON.stringify({
            name: newGroup.name,
            purpose: newGroup.purpose,
            frequency: newGroup.frequency
          })
        });
        if (data?.group?.id) {
          setGroups((prev) =>
            prev.map((group) =>
              group.id === tempId
                ? { ...group, id: String(data.group.id), createdAt: group.createdAt }
                : group
            )
          );
        }
      } catch (error) {
        // keep local copy and sync later
      }
    }
    setFormValues({
      name: "",
      purpose: "",
      frequency: "monthly"
    });
    setAddMembers(false);
    setMemberInput({ name: "", phone: "", info: "" });
    setMembers([]);
  };

  const handleAddMember = () => {
    const next = {
      name: safeMemberInput.name.trim(),
      phone: safeMemberInput.phone.trim(),
      info: safeMemberInput.info.trim()
    };
    if (!next.name) {
      return;
    }
    if (!isValidPhone(next.phone)) {
      return;
    }
    const alreadyExists = safeMembers.some(
      (member) => member.name.toLowerCase() === next.name.toLowerCase()
    );
    setMemberInput({ name: "", phone: "", info: "" });
    if (!alreadyExists) {
      setMembers((prev) => [...(Array.isArray(prev) ? prev : []), next]);
    }
  };

  const handleRemoveMember = (member) => {
    setMembers((prev) => (Array.isArray(prev) ? prev : []).filter((item) => item.name !== member.name));
  };

  const filteredGroups = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return groups;
    }
    return groups.filter((group) => {
      const memberSearch = Array.isArray(group.members)
        ? group.members
            .map((member) => `${member.name} ${member.phone} ${member.info}`.trim())
            .join(" ")
        : "";
      const haystack = [
        group.name,
        group.purpose,
        group.frequency,
        memberSearch
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [groups, searchTerm]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      return;
    }
    let isMounted = true;
    apiRequest("/groups")
      .then((data) => {
        if (!isMounted || !data?.groups) {
          return;
        }
        setGroups((prev) => {
          const prevById = new Map(prev.map((group) => [String(group.id), group]));
          return data.groups.map((group) => {
            const existing = prevById.get(String(group.id));
            return {
              id: String(group.id),
              name: group.name,
              purpose: group.purpose,
              frequency: group.frequency || "monthly",
              createdAt: group.createdAt || new Date().toISOString(),
              members: existing?.members || []
            };
          });
        });
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, []);

  const sortedGroups = useMemo(() => {
    return [...filteredGroups].sort((a, b) => {
      const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return timeB - timeA;
    });
  }, [filteredGroups]);

  const groupTotals = useMemo(() => {
    return transactions.reduce((totals, transaction) => {
      const groupId = String(transaction.groupId);
      const parsed = parseMpesaMessage(transaction.message);
      const amount = parsed.amount || 0;
      totals[groupId] = (totals[groupId] || 0) + amount;
      return totals;
    }, {});
  }, [transactions]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const totalPages = Math.max(1, Math.ceil(sortedGroups.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedGroups = sortedGroups.slice(startIndex, startIndex + pageSize);

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
            <Breadcrumb.Item>
              <FontAwesomeIcon icon={faHome} />
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Groups</Breadcrumb.Item>
          </Breadcrumb>
          <h4>Groups</h4>
          <p className="mb-0">Create groups to update cash and M-PESA contributions.</p>
        </div>
      </div>

      <Row>
        <Col xl={5} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Create a Group</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Group name</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="name"
                    value={formValues.name}
                    placeholder="e.g. Youth Fellowship"
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Purpose</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="purpose"
                    value={formValues.purpose}
                    placeholder="What is this group for?"
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Contribution frequency</Form.Label>
                  <Form.Select
                    name="frequency"
                    value={formValues.frequency}
                    onChange={handleChange}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="one-time">One-time</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="add-members-toggle"
                    label="Add members (optional)"
                    checked={addMembers}
                    onChange={(event) => {
                      const enabled = event.target.checked;
                      setAddMembers(enabled);
                      if (!enabled) {
                        setMemberInput({ name: "", phone: "", info: "" });
                      }
                    }}
                  />
                </Form.Group>
                {addMembers ? (
                  <Form.Group className="mb-3">
                    <Form.Label>Members</Form.Label>
                    <div className="d-flex flex-column gap-2">
                      <Form.Control
                        type="text"
                        name="memberName"
                        value={safeMemberInput.name}
                        placeholder="Name"
                        onChange={(event) =>
                          setMemberInput((prev) => ({ ...prev, name: event.target.value }))
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleAddMember();
                          }
                        }}
                      />
                      <Form.Control
                        type="text"
                        name="memberPhone"
                        value={safeMemberInput.phone}
                        placeholder="Phone number"
                        onChange={(event) =>
                          setMemberInput((prev) => ({ ...prev, phone: event.target.value }))
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleAddMember();
                          }
                        }}
                      />
                      {!isValidPhone(safeMemberInput.phone) ? (
                        <small className="text-danger">Enter a valid phone number.</small>
                      ) : null}
                      <Form.Control
                        type="text"
                        name="memberInfo"
                        value={safeMemberInput.info}
                        placeholder="Additional info (e.g. lower cluster)"
                        onChange={(event) =>
                          setMemberInput((prev) => ({ ...prev, info: event.target.value }))
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleAddMember();
                          }
                        }}
                      />
                      <Button variant="outline-primary" onClick={handleAddMember}>
                        Add
                      </Button>
                    </div>
                    {safeMembers.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {safeMembers.map((member) => (
                          <Button
                            key={`${member.name}-${member.phone}-${member.info}`}
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleRemoveMember(member)}
                          >
                            {member.name}
                            {member.phone ? ` · ${member.phone}` : ""}
                            {member.info ? ` · ${member.info}` : ""} &times;
                          </Button>
                        ))}
                      </div>
                    ) : null}
                  </Form.Group>
                ) : null}
                <Button variant="primary" type="submit" disabled={!canSave}>
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add group
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={7} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">Your groups</h5>
              <span className="text-muted d-flex align-items-center">
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                {filteredGroups.length} groups
              </span>
            </Card.Header>
            <Card.Body className="pt-0">
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 py-3">
                <Form.Control
                  type="text"
                  placeholder="Search groups by name or purpose"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
                <div className="d-flex align-items-center gap-2">
                  <small className="text-muted text-nowrap">
                    {filteredGroups.length} result{filteredGroups.length === 1 ? "" : "s"}
                  </small>
                  <Form.Select
                    value={pageSize}
                    onChange={(event) => setPageSize(Number(event.target.value))}
                  >
                    <option value={5}>5 / page</option>
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                  </Form.Select>
                </div>
              </div>
              <Table responsive className="table-centered align-items-center mb-0">
                <thead className="thead-light">
                  <tr>
                    <th className="border-0">Group</th>
                    <th className="border-0">Amount</th>
                    <th className="border-0 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedGroups.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-4 text-muted">
                        {groups.length === 0
                          ? "No groups yet. Create one to start tracking contributions."
                          : "No matches for your search."}
                      </td>
                    </tr>
                  ) : (
                    paginatedGroups.map((group) => (
                      <tr key={group.id}>
                        <td>
                          <span className="fw-bold">{group.name}</span>
                        </td>
                        <td>{formatAmount(groupTotals[String(group.id)] || 0)}</td>
                        <td className="text-end">
                          <Button
                            as={Link}
                            size="sm"
                            variant="outline-primary"
                            to={Routes.GroupTransactions.path.replace(":groupId", group.id)}
                          >
                            Open
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mt-3">
                <small className="text-muted">
                  Showing {filteredGroups.length === 0 ? 0 : startIndex + 1}
                  {"-"}
                  {Math.min(startIndex + pageSize, filteredGroups.length)} of {filteredGroups.length}
                </small>
                <Pagination className="mb-0">
                  <Pagination.Prev
                    disabled={safePage <= 1}
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  />
                  {Array.from({ length: totalPages }, (_, index) => {
                    const pageNumber = index + 1;
                    const isActive = pageNumber === safePage;
                    if (
                      pageNumber !== 1 &&
                      pageNumber !== totalPages &&
                      Math.abs(pageNumber - safePage) > 1
                    ) {
                      return null;
                    }
                    return (
                      <Pagination.Item
                        key={pageNumber}
                        active={isActive}
                        onClick={() => setCurrentPage(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    );
                  })}
                  <Pagination.Next
                    disabled={safePage >= totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  />
                </Pagination>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};
